import { RefObject, useEffect } from 'react';

interface UseManualTouchScrollOptions {
  enabled: boolean;
  containerRef: RefObject<HTMLElement>;
}

const FRICTION = 0.95;
const MIN_VELOCITY = 0.3;
const FRAME_MS = 16;

export const useManualTouchScroll = ({
  enabled,
  containerRef,
}: UseManualTouchScrollOptions) => {
  useEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el) return;

    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;
    let frame = 0;

    const stopMomentum = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      stopMomentum();
      lastY = touch.clientY;
      lastTime = e.timeStamp;
      velocity = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch || !e.cancelable) return;
      e.preventDefault();
      const delta = lastY - touch.clientY;
      const elapsed = Math.max(1, e.timeStamp - lastTime);
      el.scrollTop += delta;
      velocity = (delta / elapsed) * FRAME_MS;
      lastY = touch.clientY;
      lastTime = e.timeStamp;
    };

    const onTouchEnd = () => {
      const step = () => {
        if (Math.abs(velocity) < MIN_VELOCITY) {
          frame = 0;
          return;
        }
        el.scrollTop += velocity;
        velocity *= FRICTION;
        frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', stopMomentum, { passive: true });

    return () => {
      stopMomentum();
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', stopMomentum);
    };
  }, [enabled, containerRef]);
};
